/**
 * One AI voice call on the Lead timeline: the recording (our stored copy,
 * fetched with the signed-in user's token — an <audio src> can't send it) and
 * the transcript split into Agent / Customer lines.
 *
 * The audio is only downloaded when the user presses Play, so the timeline's
 * 3-second poll never re-fetches it.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { parseTranscript } from '../lib/triage';

const PREVIEW_LINES = 4;

export default function VoiceCallCard({ interaction, label }) {
  const { id, transcript, recordingUrl, agentToolCalls } = interaction;
  const webTest = !!agentToolCalls?.web_test;
  const seconds = agentToolCalls?.duration_seconds;
  const lines = useMemo(() => parseTranscript(transcript), [transcript]);
  const [expanded, setExpanded] = useState(false);
  const [audio, setAudio] = useState({ state: 'idle', url: null });

  useEffect(() => () => { if (audio.url) URL.revokeObjectURL(audio.url); }, [audio.url]);

  const loadRecording = async () => {
    setAudio({ state: 'loading', url: null });
    try {
      const res = await api.get(`/v1/interactions/${id}/recording`, { responseType: 'blob' });
      setAudio({ state: 'ready', url: URL.createObjectURL(res.data) });
    } catch {
      setAudio({ state: 'error', url: null });
    }
  };

  const shown = expanded ? lines : lines.slice(0, PREVIEW_LINES);
  const duration = seconds != null
    ? ` · ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
    : '';

  return (
    <div style={{
      alignSelf: 'stretch', padding: 12, borderRadius: 'var(--radius-md)',
      background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', fontSize: 13,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>📞 AI voice call</span>
        {webTest && <span className="pill warning filled" title="Browser mic test — test data">MIC TEST</span>}
        <span className="mono text-xs text-muted" style={{ marginLeft: 'auto' }}>{label}{duration}</span>
      </div>

      {recordingUrl ? (
        <div style={{ marginBottom: 10 }}>
          {audio.state === 'ready' ? (
            <audio controls autoPlay src={audio.url} style={{ width: '100%', height: 36 }} />
          ) : (
            <button type="button" className="btn small" disabled={audio.state === 'loading'} onClick={loadRecording}>
              {audio.state === 'loading' ? 'Loading recording…'
                : audio.state === 'error' ? '↻ Recording unavailable — retry'
                : '▶ Play recording'}
            </button>
          )}
        </div>
      ) : (
        <div className="text-xs text-muted" style={{ marginBottom: 10 }}>
          Recording not received yet — it arrives a minute or so after the call ends.
        </div>
      )}

      {lines.length === 0 ? (
        <div className="text-xs text-muted" style={{ fontStyle: 'italic' }}>No transcript for this call.</div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {shown.map((l) => (
              <div key={l.index} style={{ display: 'flex', gap: 8, lineHeight: 1.5 }}>
                <span className="mono text-xs" style={{
                  minWidth: 70, paddingTop: 2,
                  color: l.speaker === 'CUSTOMER' ? 'var(--accent, inherit)' : 'var(--text-muted)',
                }}>
                  {l.speaker === 'CUSTOMER' ? 'Customer' : l.speaker ? 'Agent' : ''}
                </span>
                <span>{l.text}</span>
              </div>
            ))}
          </div>
          {lines.length > PREVIEW_LINES && (
            <button type="button" className="btn small" style={{ marginTop: 8 }}
              onClick={() => setExpanded((v) => !v)}>
              {expanded ? 'Show less' : `Show full transcript (${lines.length} lines)`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
